import React, { useState } from 'react'
import { LazyImage } from './LazyImage'
import Lightbox from 'yet-another-react-lightbox'
import Zoom from 'yet-another-react-lightbox/plugins/zoom'
import Fullscreen from 'yet-another-react-lightbox/plugins/fullscreen'
import 'yet-another-react-lightbox/styles.css'
import PriceModal from './PriceModal'
import RejectModal from './RejectModal'
import RestoreModal from './RestoreModal'
import OrderActionsApi from '../api/orderActionsApi'
import { useToastContext } from '../contexts/ToastContext'
import { useOrdersStore } from '../store/ordersStore'
import type { Order } from '../store/ordersStore'

interface ProcessingType {
  id: number
  name: string
  price?: string
}

interface OrderCardProps {
  order: Order
  viewMode: 'grid' | 'list'
  onClick: (orderId: string) => void
  formatDate: (dateString: string) => string
}

const OrderCard: React.FC<OrderCardProps> = ({ 
  order, 
  viewMode, 
  onClick, 
  formatDate 
}) => {
  const [showMenu, setShowMenu] = useState(false)
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [showAllProcessingTypes, setShowAllProcessingTypes] = useState(false)
  const [menuPosition, setMenuPosition] = useState<'top' | 'bottom'>('top')
  
  // Состояния для модальных окон
  const [showPriceModal, setShowPriceModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [isActionLoading, setIsActionLoading] = useState(false)
  const [statusHighlight, setStatusHighlight] = useState<'success' | 'error' | null>(null)
  
  // Toast контекст для уведомлений
  const { showSuccess, showError } = useToastContext()
  
  // Глобальный store для обновления кеша
  const { updateOrderInGlobalCache, recalculateCounts, allOrders } = useOrdersStore()
  
  // Получаем актуальный заказ из глобального кеша
  const currentOrder = allOrders.find((o: Order) => o.id === order.id) || order
  
  // Функция для получения красивого названия заказа
  const getOrderDisplayName = (maxLength: number = 50) => {
    const title = currentOrder.title || `Заказ #${currentOrder.id}`
    if (title.length <= maxLength) return title
    return title.substring(0, maxLength - 3) + '...'
  }

  // Функция форматирования даты как в мини-приложении
  const formatDateSafe = (dateString: string): string => {
    if (!dateString) return 'Дата не указана'
    
    try {
      const date = new Date(dateString)
      // Проверяем, что дата валидна
      if (isNaN(date.getTime())) {
        return 'Дата не указана'
      }
      return date.toLocaleString('ru-RU', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    } catch (error) {
      return 'Дата не указана'
    }
  }

  // Цвета статусов - как в мини-приложении
  const statusColors = {
    'new': 'bg-emerald-100 text-emerald-800',
    'watching': 'bg-blue-100 text-blue-800',
    'calculating': 'bg-yellow-100 text-yellow-800',
    'price_given': 'bg-purple-100 text-purple-800',
    'pending_confirmation': 'bg-orange-100 text-orange-800',
    'executing': 'bg-indigo-100 text-indigo-800',
    'defect': 'bg-red-100 text-red-800',
    'rejected': 'bg-gray-100 text-gray-800',
    'archived': 'bg-gray-100 text-gray-800',
    'completed': 'bg-green-100 text-green-800'
  }

  const statusTexts = {
    'new': 'Новый',
    'watching': 'Просмотр',
    'calculating': 'На просчете',
    'price_given': 'Оценен',
    'pending_confirmation': 'Ждет подтверждения',
    'executing': 'Выполняется',
    'defect': 'Брак',
    'rejected': 'Отказ',
    'archived': 'Архив',
    'completed': 'Завершен'
  }

  const getStatusClasses = (status: string) => {
    let baseClasses = statusColors[status as keyof typeof statusColors] || 'bg-gray-100 text-gray-800'
    
    // Изменяем цвет для pending_confirmation в зависимости от состояния подтверждения
    if (status === 'pending_confirmation' && currentOrder.confirmation_state) {
      switch (currentOrder.confirmation_state) {
        case 'accepted':
          baseClasses = 'bg-green-100 text-green-800'
          break
        case 'modified':
          baseClasses = 'bg-yellow-100 text-yellow-800'
          break
        case 'declined':
          baseClasses = 'bg-red-100 text-red-800'
          break
        default:
          baseClasses = 'bg-orange-100 text-orange-800'
      }
    }
    
    // Добавляем подсветку при успешном действии или ошибке
    if (statusHighlight === 'success') {
      return 'bg-green-100 text-green-800 ring-2 ring-green-300 ring-opacity-50'
    } else if (statusHighlight === 'error') {
      return 'bg-red-100 text-red-800 ring-2 ring-red-300 ring-opacity-50'
    }
    
    return baseClasses
  }

  const getStatusText = (status: string) => {
    const baseText = statusTexts[status as keyof typeof statusTexts] || status
    
    // Добавляем индикатор состояния подтверждения для pending_confirmation
    if (status === 'pending_confirmation' && currentOrder.confirmation_state) {
      switch (currentOrder.confirmation_state) {
        case 'accepted':
          return `${baseText} ✅`
        case 'modified':
          return `${baseText} ✏️`
        case 'declined':
          return `${baseText} ❌`
        default:
          return baseText
      }
    }
    
    return baseText
  }

  // Извлечение количества из названия - как в мини-приложении
  const extractQuantityFromTitle = (title: string): number => {
    if (!title) return 1
    
    const patterns = [
      /(\d+)\s*шт/i,
      /(\d+)\s*штук/i,
      /(\d+)\s*деталей/i,
      /(\d+)\s*детали/i,
      /(\d+)\s*pcs/i,
      /(\d+)\s*экз/i
    ]
    
    for (const pattern of patterns) {
      const match = title.match(pattern)
      if (match) {
        return parseInt(match[1], 10)
      }
    }
    
    return 1
  }

  const quantity = order.quantity || extractQuantityFromTitle(order.title || '')

  // Функция для определения нужно ли показывать дедлайн
  const shouldShowDeadline = (order: Order) => {
    const hasAcceptedConditions = order.status === 'executing' || order.status === 'defect' || order.status === 'completed' || order.status === 'rejected'
    const hasAcceptedConfirmation = order.status === 'pending_confirmation' && order.confirmation_state === 'accepted'
    
    return (hasAcceptedConditions || hasAcceptedConfirmation) && order.completion_deadline_formatted
  }

  // Функция для определения цвета текста дедлайна
  const getDeadlineTextColor = (order: Order) => {
    if (!order.days_remaining && order.days_remaining !== 0) return 'text-gray-800'
    
    if (order.days_remaining < 0) return 'text-red-600'      // Просрочено
    if (order.days_remaining === 0) return 'text-orange-600' // Сегодня
    if (order.days_remaining <= 3) return 'text-red-500'     // Срочно
    if (order.days_remaining <= 7) return 'text-yellow-600'  // Предупреждение
    return 'text-green-600'                                  // Нормально
  }

  const handleMenuClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    
    // Всегда открываем меню над точками
    setMenuPosition('top')
    
    setShowMenu(!showMenu)
  }

  const handleMenuAction = async (action: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setShowMenu(false)
    
    if (isActionLoading) return
    
    try {
      setIsActionLoading(true)
      
      if (action === 'calculate') {
        await OrderActionsApi.takeOrderForCalculation(String(order.id))
        
        // НЕМЕДЛЕННО обновляем глобальный кеш
        updateOrderInGlobalCache(String(order.id), {
          status: 'calculating',
          updated_at: new Date().toISOString()
        })
        
        // Пересчитываем счетчики
        recalculateCounts()
        

        
        // Показываем Toast уведомление
        showSuccess('Заказ взят на просчет', `"${getOrderDisplayName()}" успешно переведен в статус "На просчете"`)
        
        // Подсвечиваем статус на карточке
        setStatusHighlight('success')
        setTimeout(() => setStatusHighlight(null), 2000)
      } else if (action === 'reject') {
        setShowRejectModal(true)
        return // Не сбрасываем loading для модального окна
      } else if (action === 'price') {
        setShowPriceModal(true)
        return // Не сбрасываем loading для модального окна
      } else if (action === 'restore') {
        setShowRestoreModal(true)
        return // Не сбрасываем loading для модального окна
      }
    } catch (error: any) {

      
      // Показываем понятное сообщение пользователю через Toast
      let errorTitle = 'Ошибка выполнения действия'
      let errorMessage = 'Произошла ошибка при выполнении действия'
      
      if (error.message?.includes('offer_id')) {
        errorTitle = 'Заказ не готов к просчету'
        errorMessage = 'Заказ еще не готов к просчету. Попробуйте позже.'
      } else if (error.message?.includes('watching')) {
        errorTitle = 'Неверный статус заказа'
        errorMessage = 'Заказ должен быть в статусе "Просмотр" для взятия на просчет'
      } else if (error.message?.includes('не найден')) {
        errorTitle = 'Заказ не найден'
        errorMessage = 'Заказ не найден или недоступен'
      } else if (error.message?.includes('reject_error')) {
        errorTitle = 'Ошибка отказа'
        errorMessage = 'Не удалось отказаться от заказа. Попробуйте позже.'
      } else if (error.message) {
        errorMessage = error.message
      }
      
      showError(errorTitle, errorMessage)
      
      // Подсвечиваем статус красным при ошибке
      setStatusHighlight('error')
      setTimeout(() => setStatusHighlight(null), 2000)
    } finally {
      setIsActionLoading(false)
    }
  }

  // Обработчик отправки цены
  const handlePriceSubmit = async (priceData: { price: number; delivery_time: number; comment: string }) => {
    try {
      await OrderActionsApi.submitPrice(String(order.id), priceData)
      
      // НЕМЕДЛЕННО обновляем глобальный кеш
      updateOrderInGlobalCache(String(order.id), {
        status: 'price_given',
        price: String(priceData.price),
        submitted_price: priceData.price,
        submitted_delivery_time: priceData.delivery_time,
        submitted_comment: priceData.comment,
        updated_at: new Date().toISOString()
      })
      
      // Пересчитываем счетчики
      recalculateCounts()
      

      
      // Показываем Toast уведомление
      showSuccess('Цена отправлена', `Цена ${priceData.price} руб. для "${getOrderDisplayName()}" успешно отправлена`)
      
      // Подсвечиваем статус на карточке
      setStatusHighlight('success')
      setTimeout(() => setStatusHighlight(null), 2000)
    } catch (error: any) {

      throw error // Пробрасываем ошибку в модальное окно
    }
  }

  // Обработчик отказа от заказа
  const handleRejectSubmit = async (reason: string) => {
    try {
      // Если заказ в статусе 'new', сначала переводим его в 'watching'
      if (currentOrder.status === 'new') {

        await OrderActionsApi.markOrderAsViewed(String(order.id))
        
        // Обновляем локальный кеш
        updateOrderInGlobalCache(String(order.id), {
          status: 'watching',
          updated_at: new Date().toISOString()
        })
      }
      
      // Теперь отказываемся от заказа
      await OrderActionsApi.rejectOrder(String(order.id), { reason })
      
      // НЕМЕДЛЕННО обновляем глобальный кеш
      updateOrderInGlobalCache(String(order.id), {
        status: 'rejected',
        updated_at: new Date().toISOString()
      })
      
      // Пересчитываем счетчики
      recalculateCounts()
      

      
      // Показываем Toast уведомление
      showSuccess('Заказ отклонен', `"${getOrderDisplayName()}" успешно отклонен`)
      
      // Подсвечиваем статус на карточке
      setStatusHighlight('success')
      setTimeout(() => setStatusHighlight(null), 2000)
    } catch (error: any) {

      
      // Показываем понятное сообщение пользователю через Toast
      let errorTitle = 'Ошибка отказа от заказа'
      let errorMessage = 'Произошла ошибка при отказе от заказа'
      
      if (error.message?.includes('не найден')) {
        errorTitle = 'Заказ не найден'
        errorMessage = 'Заказ не найден или недоступен для отказа'
      } else if (error.message?.includes('не может быть отклонен')) {
        errorTitle = 'Невозможно отказаться'
        errorMessage = 'Заказ находится в статусе, который не позволяет отказ'
      } else if (error.message) {
        errorMessage = error.message
      }
      
      showError(errorTitle, errorMessage)
      
      // Подсвечиваем статус красным при ошибке
      setStatusHighlight('error')
      setTimeout(() => setStatusHighlight(null), 2000)
      
      throw error // Пробрасываем ошибку в модальное окно
    }
  }

  // Обработчик восстановления заказа
  const handleRestoreSubmit = async (reason: string) => {
    try {
      await OrderActionsApi.restoreOrder(String(order.id), reason)
      
      // НЕМЕДЛЕННО обновляем глобальный кеш (восстанавливаем в статус calculating)
      updateOrderInGlobalCache(String(order.id), {
        status: 'calculating',
        updated_at: new Date().toISOString()
      })
      
      // Пересчитываем счетчики
      recalculateCounts()
      

      
      // Показываем Toast уведомление
      showSuccess('Заказ восстановлен', `"${getOrderDisplayName()}" успешно восстановлен`)
      
      // Подсвечиваем статус на карточке
      setStatusHighlight('success')
      setTimeout(() => setStatusHighlight(null), 2000)
    } catch (error: any) {

      
      // Показываем понятное сообщение пользователю через Toast
      let errorTitle = 'Ошибка восстановления заказа'
      let errorMessage = 'Произошла ошибка при восстановлении заказа'
      
      if (error.message?.includes('не найден')) {
        errorTitle = 'Заказ не найден'
        errorMessage = 'Заказ не найден или недоступен для восстановления'
      } else if (error.message?.includes('не может быть восстановлен')) {
        errorTitle = 'Невозможно восстановить'
        errorMessage = 'Заказ находится в статусе, который не позволяет восстановление'
      } else if (error.message) {
        errorMessage = error.message
      }
      
      showError(errorTitle, errorMessage)
      
      // Подсвечиваем статус красным при ошибке
      setStatusHighlight('error')
      setTimeout(() => setStatusHighlight(null), 2000)
      
      throw error // Пробрасываем ошибку в модальное окно
    }
  }

  // Получение доступных действий в зависимости от статуса заказа
  const getAvailableActions = () => {
    switch (currentOrder.status) {
      case 'new':
        // Для новых заказов: можно взять на просчет (автоматически перейдет через watching) или отказаться
        return [
          {
            id: 'calculate',
            label: 'Взять на просчет',
            icon: 'calculator-outline',
            className: 'text-slate-700 hover:bg-slate-50'
          },
          {
            id: 'reject',
            label: 'Отказаться',
            icon: 'close-outline',
            className: 'text-red-600 hover:bg-red-50'
          }
        ]
      case 'watching':
        // Для заказов на просмотре: можно взять на просчет или отказаться
        return [
          {
            id: 'calculate',
            label: 'Взять на просчет',
            icon: 'calculator-outline',
            className: 'text-slate-700 hover:bg-slate-50'
          },
          {
            id: 'reject',
            label: 'Отказаться',
            icon: 'close-outline',
            className: 'text-red-600 hover:bg-red-50'
          }
        ]
      case 'calculating':
        // Для заказов на просчете: можно предложить цену или отказаться
        return [
          {
            id: 'price',
            label: 'Предложить цену',
            icon: 'cash-outline',
            className: 'text-green-600 hover:bg-green-50'
          },
          {
            id: 'reject',
            label: 'Отказаться',
            icon: 'close-outline',
            className: 'text-red-600 hover:bg-red-50'
          }
        ]
      case 'price_given':
        // Для оцененных заказов: можно изменить цену или отказаться
        return [
          {
            id: 'price',
            label: 'Изменить цену',
            icon: 'create-outline',
            className: 'text-blue-600 hover:bg-blue-50'
          },
          {
            id: 'reject',
            label: 'Отказаться',
            icon: 'close-outline',
            className: 'text-red-600 hover:bg-red-50'
          }
        ]
      case 'pending_confirmation':
        // Ожидает подтверждения - никаких действий не доступно
        return []
      case 'executing':
        // В работе - никаких действий не доступно
        return []
      case 'defect':
        // Брак - никаких действий не доступно
        return []
      case 'rejected':
        // Для отклоненных заказов: можно восстановить
        return [
          {
            id: 'restore',
            label: 'Восстановить',
            icon: 'refresh-outline',
            className: 'text-blue-600 hover:bg-blue-50'
          }
        ]
      case 'archived':
      case 'completed':
        // Для завершенных заказов: никаких действий
        return []
      default:
        return []
    }
  }

  return (
    <div 
      className="bg-white rounded-2xl shadow-md overflow-visible hover:shadow-lg transition-shadow duration-300"
      data-order-id={order.id}
    >
      {/* Заголовок с статусом */}
      <div className="flex items-center justify-between p-4 pb-3 border-b border-gray-100">
        <h3 className="font-semibold text-gray-900 text-lg">
          {order.title || `Заказ ${order.id}`}
        </h3>
        <span className={`px-3 py-1 rounded-full text-xs font-medium transition-all duration-300 ${getStatusClasses(currentOrder.status)}`}>
          {getStatusText(currentOrder.status)}
        </span>
      </div>

      <div className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          {/* Изображение чертежа - сверху на мобильных, слева на десктопе */}
          <div className="flex-shrink-0 w-full sm:w-32 h-32 bg-white rounded-lg flex items-center justify-center border border-gray-200">
            {order.preview_image ? (
              <LazyImage
                src={order.preview_image}
                alt={`Чертеж ${order.title || 'заказа'}`}
                className="object-contain w-full h-full rounded-lg cursor-pointer bg-white hover:opacity-90 transition-opacity"
                onClick={(e) => {
                  e.stopPropagation()
                  setLightboxOpen(true)
                }}
              />
            ) : (
              <div className="text-slate-400 text-center">
                <ion-icon name="image-outline" style={{ fontSize: '2.5rem' }}></ion-icon>
                <div className="text-sm mt-2">Нет изображения</div>
              </div>
            )}
          </div>
          
          {/* Информация о заказе - справа */}
          <div className="flex-grow">
            {/* Материал исполнителя */}
            <div className="mb-3">
              <div className="text-sm text-gray-600">
                {order.description ? (
                  <>
                    <span className="font-semibold">Материал:</span> {order.description.replace(/^(Материал исполнителя|Материал):?\s*/i, '')}
                  </>
                ) : (
                  <span className="text-gray-400">Материал не указан</span>
                )}
              </div>
            </div>
            
            {/* Количество и дата */}
            <div className="flex items-center gap-6 text-sm text-gray-500 mb-3">
              <div className="flex items-center gap-1">
                <ion-icon name="cube-outline" style={{ fontSize: '1rem' }}></ion-icon>
                <span>{quantity} шт</span>
              </div>
              <div className="flex items-center gap-1">
                <ion-icon name="time-outline" style={{ fontSize: '1rem' }}></ion-icon>
                <span>{formatDateSafe(order.updated_at || '')}</span>
              </div>
            </div>
            

            
            {/* Типы обработки */}
            <div className="mb-4">
              {order.processing_types && order.processing_types.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {(showAllProcessingTypes ? order.processing_types : order.processing_types.slice(0, 2)).map((type, index) => (
                    <span 
                      key={index}
                      className="bg-blue-100 text-blue-800 text-xs px-3 py-1 rounded-full"
                    >
                      {typeof type === 'string' ? type : type.name}
                    </span>
                  ))}
                  {order.processing_types.length > 2 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation()
                        setShowAllProcessingTypes(!showAllProcessingTypes)
                      }}
                      className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full hover:bg-gray-200 transition-colors cursor-pointer"
                    >
                      {showAllProcessingTypes 
                        ? 'Скрыть' 
                        : `+${order.processing_types.length - 2} еще`
                      }
                    </button>
                  )}
                </div>
              ) : (
                <span className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                  На усмотрение исполнителя
                </span>
              )}
            </div>
            
            {/* Кнопки внизу */}
            <div className="flex justify-between items-center">
              <div className="flex items-center gap-3">
                <button 
                  onClick={(e) => {
                    e.stopPropagation()
                    onClick(String(order.id))
                  }}
                  className="flex items-center gap-1 bg-gray-50 hover:bg-gray-100 text-gray-700 text-xs font-medium px-3 py-1.5 rounded-md transition-all duration-200 border border-gray-200 hover:border-gray-300"
                >
                  <ion-icon name="document-text-outline" style={{ fontSize: '0.875rem' }}></ion-icon>
                  Подробнее
                </button>
                
                {/* Дедлайн справа от кнопки "Подробнее" */}
                {shouldShowDeadline(currentOrder) && (
                  <div className={`flex items-center gap-1 ${(currentOrder.status === 'completed' || currentOrder.status === 'rejected') ? 'line-through text-gray-400' : ''}`}>
                    <ion-icon name="calendar-outline" style={{ fontSize: '0.875rem', color: (currentOrder.status === 'completed' || currentOrder.status === 'rejected') ? '#9ca3af' : '#6b7280' }}></ion-icon>
                    <span className={`text-xs font-medium ${(currentOrder.status === 'completed' || currentOrder.status === 'rejected') ? 'text-gray-400' : getDeadlineTextColor(currentOrder)}`}>
                      Сдать до: {currentOrder.completion_deadline_formatted}
                      {currentOrder.deadline_status && currentOrder.deadline_status !== currentOrder.completion_deadline_formatted && (
                        <span className="ml-1">({currentOrder.deadline_status})</span>
                      )}
                    </span>
                  </div>
                )}
              </div>

              {/* Меню с тремя точками */}
              {currentOrder.status !== 'pending_confirmation' && currentOrder.status !== 'executing' && currentOrder.status !== 'defect' && currentOrder.status !== 'completed' && (
                <div className="relative">
                  <button 
                    onClick={handleMenuClick}
                    className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                  >
                    <ion-icon name="ellipsis-horizontal" style={{ fontSize: '1.25rem' }}></ion-icon>
                  </button>

                {/* Выпадающее меню */}
                {showMenu && (
                  <>
                    <div 
                      className="fixed inset-0 z-40" 
                      onClick={() => setShowMenu(false)}
                    ></div>
                    <div className={`absolute right-0 ${menuPosition === 'top' ? 'bottom-full mb-1' : 'top-full mt-1'} bg-white border border-slate-200 rounded-lg shadow-lg py-2 z-50 min-w-[160px] animate-in fade-in duration-200`}>
                      {getAvailableActions().map((action, index) => (
                        <React.Fragment key={action.id}>
                          {index > 0 && <div className="border-t border-slate-100 my-1"></div>}
                          <button
                            onClick={(e) => handleMenuAction(action.id, e)}
                            disabled={isActionLoading}
                            className={`w-full px-3 py-2 text-left text-sm transition-colors flex items-center gap-2 disabled:opacity-50 ${action.className}`}
                          >
                            <ion-icon name={action.icon} style={{ fontSize: '1rem' }}></ion-icon>
                            {action.label}
                            {isActionLoading && (
                              <svg className="animate-spin ml-auto h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                              </svg>
                            )}
                          </button>
                        </React.Fragment>
                      ))}
                    </div>
                  </>
                )}
              </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Лайтбокс для полноэкранного просмотра изображения с зумом и мобильной поддержкой */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        plugins={[Zoom, Fullscreen]}
        slides={order.preview_image ? [
          {
            src: order.preview_image,
            alt: `Чертеж ${order.title || 'заказа'}`,
          }
        ] : []}
        styles={{
          container: { 
            backgroundColor: "rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(20px)"
          }
        }}
        carousel={{
          finite: true,
          preload: 2,
          padding: "16px",
          spacing: "30%",
          imageFit: "contain"
        }}
        controller={{
          closeOnBackdropClick: true,
          closeOnPullDown: true,
          closeOnPullUp: true,
          touchAction: "pan-y"
        }}
        zoom={{
          maxZoomPixelRatio: 3,
          zoomInMultiplier: 2,
          doubleTapDelay: 300,
          doubleClickDelay: 300,
          wheelZoomDistanceFactor: 100,
          pinchZoomDistanceFactor: 100,
          scrollToZoom: true
        }}
      />

      {/* Модальные окна */}
      <PriceModal
        isOpen={showPriceModal}
        onClose={() => setShowPriceModal(false)}
        onSubmit={handlePriceSubmit}
        orderId={String(order.id)}
        orderTitle={order.title || undefined}
        isEdit={currentOrder.status === 'price_given'}
        initialPrice={order.submitted_price}
        initialDeliveryTime={order.submitted_delivery_time}
        initialComment={order.submitted_comment || ""}
      />

      <RejectModal
        isOpen={showRejectModal}
        onClose={() => setShowRejectModal(false)}
        onSubmit={handleRejectSubmit}
        orderId={String(order.id)}
        orderTitle={order.title || undefined}
      />

      <RestoreModal
        isOpen={showRestoreModal}
        onClose={() => setShowRestoreModal(false)}
        onSubmit={handleRestoreSubmit}
        orderId={String(order.id)}
        orderTitle={order.title || undefined}
      />
    </div>
  )
}

export default OrderCard